import * as React from 'react';
import firebase from 'client/utils/firebase';
import { introwise } from 'common/types';
import { convertFirestoreUser } from 'client/utils/conversions';
import errorHandler from 'client/utils/error-handler';

type OnLoginSuccessFunc = (userId: string) => void;

type UserContextType = {
  user: introwise.User;
  userId: string;
  loggingIn: boolean;
  loading: boolean;
  showSignInModal: (onLoginSuccess: OnLoginSuccessFunc) => void;
  hideSignInModal: () => void;
  signInModalOpen: boolean;
  onLoginSuccess: OnLoginSuccessFunc;
};

type Props = {};

type State = UserContextType;

const UserContext = React.createContext<UserContextType>({
  user: null,
  userId: null,
  loggingIn: false,
  loading: false,
  showSignInModal: () => {},
  hideSignInModal: () => {},
  signInModalOpen: false,
  onLoginSuccess: () => {},
});

/* eslint-disable react/no-unused-state */
class UserProvider extends React.Component<Props, State> {
  state: UserContextType = {
    showSignInModal: onLoginSuccess => {
      this.setState({
        signInModalOpen: true,
        onLoginSuccess,
      });
    },
    hideSignInModal: () => {
      this.setState({
        signInModalOpen: false,
        onLoginSuccess: () => {},
      });
    },
    signInModalOpen: false,
    onLoginSuccess: () => {},
    loggingIn: true,
    loading: false,
    userId: null,
    user: null,
  };

  componentDidMount() {
    firebase.auth().onAuthStateChanged(userAuth => {
      if (this.snapshotUnsubscribe) {
        this.snapshotUnsubscribe();
      }
      this.fetchUser(userAuth);
    });
  }

  snapshotUnsubscribe: () => void;

  fetchUser(userAuth: firebase.User) {
    this.setState({ loggingIn: true });
    if (userAuth) {
      const userDataFromAuth = {
        id: userAuth.uid,
        name: userAuth.displayName,
        photo: userAuth.photoURL,
        email: userAuth.email,
        emailVerified: userAuth.emailVerified,
      } as introwise.User;
      this.setState({
        userId: userAuth.uid,
        loggingIn: false,
        loading: true,
        user: userDataFromAuth,
      });
      this.snapshotUnsubscribe = firebase
        .firestore()
        .collection('users')
        .doc(userAuth.uid)
        .onSnapshot(
          doc => {
            const userDataFromDb = doc.data();
            if (!userDataFromDb) {
              return;
            }
            const userConverted = convertFirestoreUser(userDataFromDb as introwise.User<firebase.firestore.Timestamp>);
            const user = Object.assign({}, userDataFromAuth, userConverted);
            this.setState({
              userId: userAuth.uid,
              user,
              loggingIn: false,
              loading: false,
            });
          },
          error => errorHandler.report(`Error while loading user: ${error}`),
        );
    } else {
      this.setState({
        userId: null,
        user: null,
        loggingIn: false,
        loading: false,
      });
    }
  }

  /* eslint-enable react/no-unused-state */

  render() {
    const { children } = this.props;
    return <UserContext.Provider value={this.state}>{children}</UserContext.Provider>;
  }
}

const UserProviderContainer = UserProvider;

const UserConsumer = UserContext.Consumer;

type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;
type Subtract<T, K> = Omit<T, keyof K>;

type WithUserProps = {
  user?: any;
  userId?: string;
};

const withUser = <WrappedProps extends WithUserProps>(WrappedComponent: React.ComponentType<WrappedProps>) => (
  props: Subtract<WrappedProps, WithUserProps>,
) => {
  return (
    <UserConsumer>
      {({ userId, user }) => <WrappedComponent user={user} userId={userId} {...(props as any)} />}
    </UserConsumer>
  );
};

export { UserProviderContainer as UserProvider, UserConsumer, withUser, UserContext };
